import React, { useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { UIExportModal } from "../../ui";

// Sheet column width (characters) = longest value + padding, up to the max.
const COLUMN_PADDING = 4;
const MAX_COLUMN_WIDTH = 60;

/**
 * "Export All" for an activity log screen (admin + company portals) — the
 * shared `UIExportModal` field picker, then an .xlsx of every entry passed in
 * (all pages), selected columns only. Each field's `value(entry)` reads its
 * cell. `loadEntries` (optional) fetches the entries on confirm instead —
 * the API export is the list request with `page=-1`.
 */
const ActivityLogExportModal = ({
  open,
  onClose,
  fields,
  entries,
  loadEntries,
  copy,
  cancelLabel,
}) => {
  // Checkbox state keyed by field name — starts empty, as in Reports.
  const [selectedFields, setSelectedFields] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  const selected = fields.filter((field) => selectedFields[field.name]);
  const isAllSelected = selected.length === fields.length;

  const handleClose = () => {
    setSelectedFields({});
    onClose();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const rows = loadEntries ? await loadEntries() : entries;
      if (!rows) return;
      const header = selected.map((field) => field.label);
      const body = rows.map((entry) =>
        selected.map((field) => field.value(entry)),
      );

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(copy.title.slice(0, 31), {
        views: [{ state: "frozen", ySplit: 1 }],
      });
      // Each column fits its longest value plus padding; long text (Action)
      // is capped and wraps instead of stretching the column.
      sheet.columns = header.map((label, index) => {
        const longest = body.reduce(
          (max, cells) => Math.max(max, String(cells[index] ?? "").length),
          label.length,
        );
        return { width: Math.min(longest + COLUMN_PADDING, MAX_COLUMN_WIDTH) };
      });

      // Header style as in the other sheet exports (exportCsvLinkAsXlsx).
      const headerRow = sheet.addRow(header);
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D9E1F2" },
        };
        cell.font = { bold: true };
        cell.alignment = { vertical: "middle" };
      });
      body.forEach((cells) => {
        sheet.addRow(cells).eachCell((cell) => {
          cell.alignment = { vertical: "middle", wrapText: true };
        });
      });      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        `${copy.exportFileName}-${dayjs().format("YYYY-MM-DD")}.xlsx`,
      );
      handleClose();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <UIExportModal
      open={open}
      title={copy.exportModal.title}
      description={copy.exportModal.description}
      selectAllLabel={copy.exportModal.selectAll}
      cancelLabel={cancelLabel}
      confirmLabel={copy.exportModal.confirm}
      fields={fields}
      selectedFields={selectedFields}
      isAllSelected={isAllSelected}
      isIndeterminate={selected.length > 0 && !isAllSelected}
      confirmDisabled={selected.length === 0 || isExporting}
      onClose={handleClose}
      onConfirm={handleExport}
      onFieldChange={(name, checked) =>
        setSelectedFields((prev) => ({ ...prev, [name]: checked }))
      }
      onSelectAll={(checked) =>
        setSelectedFields(
          Object.fromEntries(fields.map((field) => [field.name, checked])),
        )
      }
      maxWidth={false}
      className="activity-log__export-modal"
    />
  );
};

ActivityLogExportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.func.isRequired,
    }),
  ).isRequired,
  entries: PropTypes.arrayOf(PropTypes.object),
  /** Async — resolves the entries to export, or nothing when the request failed. */
  loadEntries: PropTypes.func,
  /** `messages[lang].activityLog` — title, exportModal, exportFileName. */
  copy: PropTypes.object.isRequired,
  cancelLabel: PropTypes.string.isRequired,
};

ActivityLogExportModal.defaultProps = {
  entries: [],
  loadEntries: null,
};

export default ActivityLogExportModal;
